import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

// Une minute : un message de support n'appelle pas de reponse a la seconde, et
// les navigateurs ralentissent de toute facon les minuteries d'un onglet cache.
const INTERVALLE_MS = 60_000;

// Le titre d'origine, lu une fois : le recomposer depuis document.title
// empilerait les prefixes « (2) (1) » au fil des mises a jour.
const TITRE_BASE = typeof document !== "undefined" ? document.title : "";

function notifier(nouveaux, dernier, ouvrir) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const corps = nouveaux > 1
    ? `${nouveaux} nouveaux messages${dernier ? ` — dernier de ${dernier.de}` : ""}`
    : dernier
      ? `De ${dernier.de}${dernier.sujet ? ` : ${dernier.sujet}` : ""}`
      : "Nouveau message";
  try {
    // `tag` remplace la notification precedente au lieu d'en empiler une par minute.
    const n = new Notification("Roast My Stats — nouveau message", { body: corps, tag: "rms-inbox" });
    n.onclick = () => {
      window.focus();
      if (ouvrir) ouvrir();
      n.close();
    };
  } catch {
    // Chrome Android refuse le constructeur hors service worker : pas de
    // notification, le badge suffit.
  }
}

/**
 * Nombre de messages non lus de la boite de reception, pour un admin connecte.
 * Notifie le bureau quand il augmente et prefixe le titre de l'onglet.
 * Renvoie [nombre, rafraichir] : la boite appelle `rafraichir` apres une lecture
 * pour que le badge retombe sans attendre la minute suivante.
 */
export default function useAdminUnread(token, actif, ouvrir) {
  const [unread, setUnread] = useState(0);
  // null = aucun releve encore : les messages deja la au chargement ne sont pas
  // « nouveaux », on ne notifie que ce qui arrive ensuite.
  const precedent = useRef(null);
  const ouvrirRef = useRef(ouvrir);
  ouvrirRef.current = ouvrir;

  const rafraichir = useCallback(async () => {
    if (!actif || !token) return;
    try {
      const res = await axios.get(`${API}/admin/inbox/unread`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const n = res.data.unread || 0;
      if (precedent.current !== null && n > precedent.current) {
        notifier(n - precedent.current, res.data.dernier, ouvrirRef.current);
      }
      precedent.current = n;
      setUnread(n);
    } catch {
      // Un rate reseau garde la derniere valeur plutot que de faire clignoter le badge.
    }
  }, [token, actif]);

  useEffect(() => {
    if (!actif) {
      precedent.current = null;
      setUnread(0);
      return undefined;
    }
    rafraichir();
    const id = setInterval(rafraichir, INTERVALLE_MS);
    const auRetour = () => { if (document.visibilityState === "visible") rafraichir(); };
    document.addEventListener("visibilitychange", auRetour);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", auRetour);
    };
  }, [actif, rafraichir]);

  useEffect(() => {
    document.title = unread > 0 ? `(${unread}) ${TITRE_BASE}` : TITRE_BASE;
  }, [unread]);

  return [unread, rafraichir];
}
